import React from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import { GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { usePagedList } from '../../../hooks/use-paged-list';
import { formatDate } from '../../../utils/date';
import { formatCurrency } from '../../../utils/format';
import { usePurchaseContractSearchController } from './context/purchase-contract-search-controller';
import { usePurchaseContractSearchState } from './context/purchase-contract-search-context';
import PurchaseContractAppService from '../../../application/platform/apm/purchase-contract/purchase-contract-app-service';
import { FilterInput, FilterSelect } from '../../../components/filter-fields';
import { booleanFilterValue } from '../../../utils/filter-options';
import { PagedGrid } from '../../../components/paged-grid';
import { AttachmentsAction, DeleteAction, EditAction } from '../../../components/row-actions';
import { PurchaseContractDrawer } from './components/purchase-contract-drawer';

const COVID_OPTIONS = [{ value: 'true', label: 'Somente COVID-19' }, { value: 'false', label: 'Demais contratos' }];

export function PurchaseContractSearchComponent() {
    const ctrl = usePurchaseContractSearchController();
    const { parameters, searchTrigger, documentTypes, drawer } = usePurchaseContractSearchState();
    const list = usePagedList(PurchaseContractAppService.list, parameters, searchTrigger, 'Não foi possível carregar os contratos');

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'contractedCompanyName', headerName: 'Contratado', flex: 1, minWidth: 240,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{params.value}</Typography>
                    {params.row.isCovid19 && (
                        <Tooltip title="Contratação COVID-19">
                            <CoronavirusIcon fontSize="small" color="error" />
                        </Tooltip>
                    )}
                </Box>
            )
        },
        { field: 'contractedCompanyDocument', headerName: 'CPF/CNPJ', width: 160 },
        { field: 'processNumber', headerName: 'Processo', width: 150 },
        { field: 'contractDeadline', headerName: 'Prazo', width: 110, valueFormatter: (params) => formatDate(params.value) },
        { field: 'amountPaid', headerName: 'Pago', width: 130, type: 'number', valueFormatter: (params) => formatCurrency(params.value) },
        { field: 'balance', headerName: 'Saldo', width: 130, type: 'number', valueFormatter: (params) => formatCurrency(params.value) },
        {
            field: 'actions', headerName: 'Ações', width: 130, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <AttachmentsAction fileCount={params.row.fileCount} onClick={() => ctrl.openDrawer(params.row)} />
                    <EditAction onClick={() => ctrl.openDrawer(params.row)} />
                    <DeleteAction onClick={() => ctrl.handleDelete(params.row)} />
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Contratos"
                    subtitle="Contratos de aquisição e seus documentos publicados no portal"
                    breadcrumbs={[{ label: 'Contratos' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={6}>
                        <FilterInput label="Contratado, CPF/CNPJ ou processo" name="input-term" value={parameters.term} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FilterInput label="Ano do prazo" name="input-year" type="number" value={parameters.year} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FilterSelect label="COVID-19" name="select-covid" value={booleanFilterValue(parameters.isCovid19)} options={COVID_OPTIONS} onChange={ctrl.handleOnChange} />
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <PagedGrid
                    columns={columns}
                    list={list}
                    parameters={parameters}
                    onPaginationChange={ctrl.handlePaginationChange}
                    addLabel="Novo Contrato"
                    onAdd={() => ctrl.openDrawer()}
                />
            </Grid>

            <PurchaseContractDrawer
                open={drawer.open}
                initial={drawer.contract}
                files={drawer.files}
                documentTypes={documentTypes}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
                onUploadFile={ctrl.handleUploadFile}
                onDeleteFile={ctrl.handleDeleteFile}
            />
        </React.Fragment>
    );
}
